(ns common.db.router
  (:require ["react" :as react]
            [clojure.spec.alpha :as spec]
            [clojure.string :as str]
            [common.error-page.view :refer [error-page]]
            [common.flags]
            [common.os :as os]
            [common.re-frame :refer [with-rf-dispatch-calls-into-fx-map]]
            [common.router :refer [router]]
            [common.url :as url]
            [mate.core :as mc]
            [mate.re-frame :as mr]
            [re-frame.core :as rf]
            [reitit.frontend :as r.fe]
            [reitit.frontend.controllers :as rfc]
            [reitit.frontend.history :as rfh]))

(defn pop-path [path]
  (let [path* (cond
                (str/ends-with? path "/")
                (subs path 0 (dec (count path)))

                (empty? path) nil

                :else
                (str/join "/" (-> path
                                  (str/split #"/")
                                  vec
                                  pop)))]
    (if (or (nil? path*)
            (try
              (r.fe/match-by-path router path*)
              (catch js/Object _)))
      path*
      (pop-path path*))))

(rf/reg-event-fx :router/navigate-up
  [(rf/inject-cofx :localstorage ["session"])]
  (fn [{:localstorage/keys [session]} [_ history]]
    (let [path (rfh/-get-path history)
          new-path (pop-path path)]
      {:fx [(cond
              (nil? session) [:router/navigate! [:common/login
                                                 {:query {:from (some->> path (str js/window.location.origin))}}]]
              new-path [:router/navigate! new-path]
              (:agencyId session) [:dispatch [:router/navigate-home]]
              :else [:router/navigate! :common/user])]})))

(rf/reg-event-fx :router/redirect
  (fn [_ [_ history]]
    {:fx [[:router/replace! [(get-in history [:data :redirect-to])
                             {:params (:path-params history)
                              :fragment (:fragment history)
                              :query (:query-params history)}]]]}))

(spec/def ::agency-id int?)

(spec/def ::advertiser-id int?)

(spec/def ::context
  (spec/keys :req-un [::agency-id ::advertiser-id]))

(rf/reg-event-fx :router/navigate-home
  [(rf/inject-cofx :localstorage ["session" "last-viewed-advertiser-id"])]
  (fn [{:localstorage/keys [session last-viewed-advertiser-id]} _]
    (let [context {:agency-id (:agencyId session)
                   :advertiser-id last-viewed-advertiser-id}]
      {:fx [(if (spec/valid? ::context context)
              [:router/navigate! [:advertiser/page {:params context}]]
              [:router/navigate! [:common/agency-selection]])]})))

(rf/reg-event-fx :router/navigate-public-scope
  (fn [{:keys [db]} [_ new-match]]
    {:db (dissoc db :controller-status)
     :fx [[:dispatch [:router/navigated-success new-match]]]}))

;; IDEA: change os=true to create.force-login=true
;; .. if true, then don't use session even if exists
;; .. move the login flow to login-page
(rf/reg-event-fx :router/navigate-user-login-scope
  [(rf/inject-cofx :localstorage ["session"])]
  (fn [{:keys [db]
        :localstorage/keys [session]} [_ new-match]]
    (js/console.log ":router/navigate-user-login-scope")
    {:db (dissoc db :controller-status)
     :fx [(if (and session (not (os/os-environment?))) ; always re-login under OS
            [:dispatch [:router/navigate-home]]
            [:dispatch [:router/navigated-success new-match]])]}))

(defn navigate-flow [agency-id session-agency-id new-match on-success-events]
  {:id :router/navigate-flow
   :rules [{:when :seen-any-of?
            :events [:resource.auth/init-failure
                     :resource.auth/refresh-token-failure]
            :halt? true
            :dispatch [:router/navigated-failure new-match]}
           {:when :seen?
            :events :resource.auth/refresh-success
            :halt? (or (nil? agency-id)
                       (= agency-id session-agency-id))
            :dispatch (if (or (nil? agency-id)
                              (= agency-id session-agency-id))
                        [:router/navigated-success new-match on-success-events]
                        [:resource.auth/switch-agency agency-id])}
           {:when :seen-any-of?
            :events [:resource.auth/switched-agency
                     :resource.auth/switch-agency-failure]
            :halt? true
            :dispatch [:router/navigated-success new-match]}]})

(rf/reg-event-fx :router/navigate-user-scope
  [(rf/inject-cofx :sub :resource.auth/init-status)
   (rf/inject-cofx :sub :router/controller-status)
   (rf/inject-cofx :localstorage ["session"])]
  (fn [{:keys [db]
        :resource.auth/keys [init-status]
        :router/keys [controller-status]
        :localstorage/keys [session]} [_ new-match on-success-events]]
    (if (:disable-navigation controller-status)
      ;; this is a relatively ugly hack. Some errors (such as during OS initialization)
      ;; lead to an error condition from where no navigation should be happening
      ;; due to the fact that the os-provider init and the portal init are running in parallel
      ;; it's possible that the os-provider triggers displaying an error and right after
      ;; portal initialization tries to navigate to advertiser/page and displays a different error
      ;; this :disable-navigation in the controller status allows to make an error state a
      ;; "dead-end", no further navigation should happen before app restart
      {}
      (let [session-agency-id (:agencyId session)
            agency-id (get-in new-match [:parameters :path :agency-id])]
        {:db (dissoc db :controller-status)
         :fx [(when-not (or (:failure init-status)
                            (and (nil? agency-id)
                                 (= :success init-status))
                            (and (= agency-id session-agency-id)
                                 (= :success init-status)))
                [:async-flow (navigate-flow agency-id session-agency-id new-match on-success-events)])
              (cond
                (= :failure init-status) [:router/navigate!
                                          [:common/login
                                           {:query {:from (some->> (:path new-match)
                                                                   (str js/window.location.origin))}}]]

                (and (or (nil? agency-id) (= agency-id session-agency-id))
                     (= :success init-status)) [:dispatch [:router/navigated-success new-match]]

                (= :success init-status) [:dispatch [:resource.auth/switch-agency agency-id]]

                (nil? init-status) [:dispatch [:resource.auth/init]])]}))))

(rf/reg-event-fx :router/navigated-success
  (fn [{:keys [db]} [_ new-match on-success-events]]
    (->
     ;; Intercepts side-effectful calls to `rf/dispatch` happening in (rfc/apply-controllers ,,,)
     ;; and conj them into the :fx part of the effect hashmap.
     (with-rf-dispatch-calls-into-fx-map
       (let [old-match (:current-route db)
             controllers (rfc/apply-controllers (:controllers old-match) new-match)]
         {:db (assoc db :current-route (assoc new-match :controllers controllers))}))
     (mr/into-fx (for [on-success-event on-success-events]
                   [:dispatch on-success-event])))))

(rf/reg-event-fx :router/navigated-failure
  (fn [_ [_ new-match]]
    (let [from (cond-> (str js/window.location.origin)
                 (:path new-match) (str (:path new-match))
                 (seq (:query-params new-match)) (str "?" (url/map->query-string (:query-params new-match))))]
      {:fx [[:router/navigate! [:common/login {:query {:from from}}]]]})))

(rf/reg-event-db :router/update-controller-status
  (fn [{:keys [controller-status] :as db} [_ status]]
    ;; if navigation is disabled, don't update the status as that would
    ;; practically be a navigation action
    (if (:disable-navigation controller-status)
      db
      (assoc db :controller-status status))))

(rf/reg-sub :router/current-route
  (fn [db]
    (:current-route db)))

(rf/reg-sub :router/parameters
  :<- [:router/current-route]
  (fn [current-route]
    (:parameters current-route)))

(rf/reg-sub :router/controller-status
  (fn [db]
    (:controller-status db)))

(rf/reg-sub :router/allowed-and-enabled?
  (fn [[_ {:keys [permissions flag]}]]
    [(rf/subscribe [:resource.user/allowed? permissions])
     (rf/subscribe [:flag/enabled flag])])
  (fn [[user-allowed flag-enabled] [_ {:keys [flag]}]]
    (and user-allowed
         (mc/implies (some? flag) flag-enabled))))

(rf/reg-sub :router/handler-name
  :<- [:router/route-data]
  (fn [route-data _]
    (:name route-data)))

(rf/reg-sub :router/view-wrapper
  :<- [:router/route-data]
  (fn [route-data _]
    (or (:view-wrapper route-data) react/Fragment)))

(rf/reg-sub :router/view-component
  :<- [:router/current-route]
  :<- [:router/controller-status]
  (fn [[current-route controller-status] _]
    (let [allowed-and-enabled? @(rf/subscribe [:router/allowed-and-enabled? (:data current-route)])]
      (if (and allowed-and-enabled? (not (:failure? controller-status)))
        (get-in current-route [:data :view])
        error-page))))

(rf/reg-sub :router/path-params
  :<- [:router/parameters]
  :<- [:router/controller-status]
  (fn [[parameters {:keys [failure-props]}] _]
    (if failure-props
      failure-props
      (:path parameters))))

(rf/reg-sub :router/query-parameters
  :<- [:router/parameters]
  (fn [parameters _]
    (:query parameters)))

(rf/reg-sub :router/fragment
  :<- [:router/parameters]
  (fn [parameters]
    (:fragment parameters)))

;; raw parameters
;; FIXME: Using this should be avoided! Coerced value (the previous sub)
;; is probably the correct always.
;; TODO: Check what the failure-props logic does?
(rf/reg-sub :router/query-parameters-pre-coercion
  :<- [:router/current-route]
  :<- [:router/controller-status]
  (fn [[current-route {:keys [failure-props]}] _]
    (if failure-props
      failure-props
      (:query-params current-route))))

(rf/reg-sub :router/route-data
  :<- [:router/current-route]
  (fn [current-route _]
    (:data current-route)))
